import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import './Calculator.css';

const Calculator = ({ show, onHide }) => {
  const [display, setDisplay] = useState('0');
  const [previousValue, setPreviousValue] = useState(null);
  const [operation, setOperation] = useState(null);
  const [waitingForNewValue, setWaitingForNewValue] = useState(false);

  // Funciones base
  const handleNumberClick = (num) => {
    if (waitingForNewValue) {
      setDisplay(String(num));
      setWaitingForNewValue(false);
    } else {
      setDisplay(display === '0' ? String(num) : display + num);
    }
  };

  const handleDecimal = () => {
    if (waitingForNewValue) {
      setDisplay('0.');
      setWaitingForNewValue(false);
    } else if (!display.includes('.')) {
      setDisplay(display + '.');
    }
  };

  const handleOperation = (op) => {
    const currentValue = parseFloat(display);

    if (previousValue === null) {
      setPreviousValue(currentValue);
    } else if (operation) {
      const result = performCalculation(previousValue, currentValue, operation);
      setDisplay(String(result));
      setPreviousValue(result);
    }

    setOperation(op);
    setWaitingForNewValue(true);
  };

  const performCalculation = (prev, current, op) => {
    switch (op) {
      case '+':
        return prev + current;
      case '-':
        return prev - current;
      case '*':
        return prev * current;
      case '/':
        return current !== 0 ? prev / current : 0;
      case '%':
        return prev % current;
      default:
        return current;
    }
  };

  const handleEquals = () => {
    if (operation && previousValue !== null) {
      const currentValue = parseFloat(display);
      const result = performCalculation(previousValue, currentValue, operation);
      setDisplay(String(result));
      setPreviousValue(null);
      setOperation(null);
      setWaitingForNewValue(true);
    }
  };

  const handleClear = () => {
    setDisplay('0');
    setPreviousValue(null);
    setOperation(null);
    setWaitingForNewValue(false);
  };

  const handleBackspace = () => {
    if (display.length > 1) {
      setDisplay(display.slice(0, -1));
    } else {
      setDisplay('0');
    }
  };

  const handleToggleSign = () => {
    const value = parseFloat(display);
    setDisplay(String(value * -1));
  };

  const handlePercentage = () => {
    const value = parseFloat(display);
    setDisplay(String(value / 100));
  };

  const handleSquareRoot = () => {
    const value = parseFloat(display);
    setDisplay(String(Math.sqrt(value)));
  };

  const handleSquare = () => {
    const value = parseFloat(display);
    setDisplay(String(value * value));
  };

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(display);
  };

  // Detectar teclas del teclado
  useEffect(() => {
    if (!show) return;

    const handleKeyPress = (e) => {
      const key = e.key;

      // Números (0-9)
      if (/^[0-9]$/.test(key)) {
        e.preventDefault();
        setDisplay(prevDisplay => {
          if (waitingForNewValue) {
            return String(key);
          } else {
            return prevDisplay === '0' ? String(key) : prevDisplay + key;
          }
        });
        setWaitingForNewValue(false);
        return;
      }

      // Operadores
      if (key === '+') {
        e.preventDefault();
        setDisplay(prevDisplay => {
          const currentValue = parseFloat(prevDisplay);
          if (previousValue === null) {
            setPreviousValue(currentValue);
          } else if (operation) {
            const result = performCalculation(previousValue, currentValue, operation);
            setPreviousValue(result);
            setDisplay(String(result));
          }
          setOperation('+');
          setWaitingForNewValue(true);
          return prevDisplay;
        });
        return;
      }
      if (key === '-' && e.location === 0) {
        e.preventDefault();
        setDisplay(prevDisplay => {
          const currentValue = parseFloat(prevDisplay);
          if (previousValue === null) {
            setPreviousValue(currentValue);
          } else if (operation) {
            const result = performCalculation(previousValue, currentValue, operation);
            setPreviousValue(result);
            setDisplay(String(result));
          }
          setOperation('-');
          setWaitingForNewValue(true);
          return prevDisplay;
        });
        return;
      }
      if (key === '*') {
        e.preventDefault();
        setDisplay(prevDisplay => {
          const currentValue = parseFloat(prevDisplay);
          if (previousValue === null) {
            setPreviousValue(currentValue);
          } else if (operation) {
            const result = performCalculation(previousValue, currentValue, operation);
            setPreviousValue(result);
            setDisplay(String(result));
          }
          setOperation('*');
          setWaitingForNewValue(true);
          return prevDisplay;
        });
        return;
      }
      if (key === '/') {
        e.preventDefault();
        setDisplay(prevDisplay => {
          const currentValue = parseFloat(prevDisplay);
          if (previousValue === null) {
            setPreviousValue(currentValue);
          } else if (operation) {
            const result = performCalculation(previousValue, currentValue, operation);
            setPreviousValue(result);
            setDisplay(String(result));
          }
          setOperation('/');
          setWaitingForNewValue(true);
          return prevDisplay;
        });
        return;
      }
      if (key === '%') {
        e.preventDefault();
        setDisplay(prevDisplay => {
          const currentValue = parseFloat(prevDisplay);
          if (previousValue === null) {
            setPreviousValue(currentValue);
          } else if (operation) {
            const result = performCalculation(previousValue, currentValue, operation);
            setPreviousValue(result);
            setDisplay(String(result));
          }
          setOperation('%');
          setWaitingForNewValue(true);
          return prevDisplay;
        });
        return;
      }

      // Decimal (. o ,)
      if (key === '.' || key === ',') {
        e.preventDefault();
        setDisplay(prevDisplay => {
          if (waitingForNewValue) {
            return '0.';
          } else if (!prevDisplay.includes('.')) {
            return prevDisplay + '.';
          }
          return prevDisplay;
        });
        setWaitingForNewValue(false);
        return;
      }

      // Enter o Igual
      if (key === 'Enter' || key === '=') {
        e.preventDefault();
        if (operation && previousValue !== null) {
          setDisplay(prevDisplay => {
            const currentValue = parseFloat(prevDisplay);
            const result = performCalculation(previousValue, currentValue, operation);
            setPreviousValue(null);
            setOperation(null);
            setWaitingForNewValue(true);
            return String(result);
          });
        }
        return;
      }

      // Backspace
      if (key === 'Backspace') {
        e.preventDefault();
        setDisplay(prevDisplay => {
          if (prevDisplay.length > 1) {
            return prevDisplay.slice(0, -1);
          } else {
            return '0';
          }
        });
        return;
      }

      // Escape
      if (key === 'Escape') {
        e.preventDefault();
        onHide();
        return;
      }

      // Delete (Limpiar)
      if (key === 'Delete') {
        e.preventDefault();
        handleClear();
        return;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [show, previousValue, operation, waitingForNewValue]);

  return (
    <Modal show={show} onHide={onHide} size="sm" centered className="calculator-modal">
      <Modal.Header closeButton className="calculator-header">
        <Modal.Title className="calculator-title">
          <i className="fas fa-calculator me-2"></i>
          Calculadora
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="calculator-body p-0">
        <div className="calculator-container">
          {/* Display */}
          <div className="calculator-display">
            <div className="display-value">{display}</div>
            {operation && previousValue !== null && (
              <div className="display-operation">
                {previousValue} {operation}
              </div>
            )}
          </div>

          {/* Ayuda de teclado */}
          <div className="keyboard-hint">
            <small>💡 Usa tu teclado: números, +, -, *, /, Enter, Backspace, Delete, Escape</small>
          </div>

          {/* Botones */}
          <div className="calculator-buttons">
            {/* Fila 1: Funciones */}
            <div className="button-row">
              <button className="calc-btn btn-function" onClick={handleClear} title="Limpiar (Delete)">
                C
              </button>
              <button className="calc-btn btn-function" onClick={handleBackspace} title="Retroceso (Backspace)">
                ⌫
              </button>
              <button className="calc-btn btn-function" onClick={handleToggleSign} title="Cambiar signo">
                +/-
              </button>
              <button className="calc-btn btn-function" onClick={handlePercentage} title="Porcentaje (%)">
                %
              </button>
            </div>

            {/* Fila 2: Científicas */}
            <div className="button-row">
              <button className="calc-btn btn-science" onClick={handleSquareRoot} title="Raíz cuadrada">
                √
              </button>
              <button className="calc-btn btn-science" onClick={handleSquare} title="Cuadrado">
                x²
              </button>
              <button className="calc-btn btn-operator" onClick={() => handleOperation('/')} title="División (/)">
                ÷
              </button>
              <button className="calc-btn btn-operator" onClick={() => handleOperation('*')} title="Multiplicación (*)">
                ×
              </button>
            </div>

            {/* Fila 3: 7, 8, 9 */}
            <div className="button-row">
              <button className="calc-btn btn-number" onClick={() => handleNumberClick(7)}>
                7
              </button>
              <button className="calc-btn btn-number" onClick={() => handleNumberClick(8)}>
                8
              </button>
              <button className="calc-btn btn-number" onClick={() => handleNumberClick(9)}>
                9
              </button>
              <button className="calc-btn btn-operator" onClick={() => handleOperation('-')} title="Resta (-)">
                −
              </button>
            </div>

            {/* Fila 4: 4, 5, 6 */}
            <div className="button-row">
              <button className="calc-btn btn-number" onClick={() => handleNumberClick(4)}>
                4
              </button>
              <button className="calc-btn btn-number" onClick={() => handleNumberClick(5)}>
                5
              </button>
              <button className="calc-btn btn-number" onClick={() => handleNumberClick(6)}>
                6
              </button>
              <button className="calc-btn btn-operator" onClick={() => handleOperation('+')} title="Suma (+)">
                +
              </button>
            </div>

            {/* Fila 5: 1, 2, 3 */}
            <div className="button-row">
              <button className="calc-btn btn-number" onClick={() => handleNumberClick(1)}>
                1
              </button>
              <button className="calc-btn btn-number" onClick={() => handleNumberClick(2)}>
                2
              </button>
              <button className="calc-btn btn-number" onClick={() => handleNumberClick(3)}>
                3
              </button>
              <button className="calc-btn btn-equals" onClick={handleEquals} style={{ gridRow: 'span 2' }} title="Igual (Enter)">
                =
              </button>
            </div>

            {/* Fila 6: 0, . */}
            <div className="button-row">
              <button className="calc-btn btn-number" onClick={() => handleNumberClick(0)} style={{ gridColumn: 'span 2' }}>
                0
              </button>
              <button className="calc-btn btn-number" onClick={handleDecimal} title="Decimal (.)">
                .
              </button>
            </div>

            {/* Fila 7: Copiar */}
            <div className="button-row">
              <button className="calc-btn btn-copy" onClick={handleCopyToClipboard} style={{ gridColumn: 'span 4' }}>
                <i className="fas fa-copy me-2"></i>
                Copiar Resultado
              </button>
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default Calculator;
